const SOCIAL_PLATFORMS = Object.freeze([
  { platform: "facebook", label: "Facebook", sortOrder: 10 },
  { platform: "instagram", label: "Instagram", sortOrder: 20 },
  { platform: "tiktok", label: "TikTok", sortOrder: 30 },
  { platform: "linkedin", label: "LinkedIn", sortOrder: 40 },
  { platform: "youtube", label: "YouTube", sortOrder: 50 },
  { platform: "x", label: "X / Twitter", sortOrder: 60 },
  { platform: "threads", label: "Threads", sortOrder: 70 },
  { platform: "pinterest", label: "Pinterest", sortOrder: 80 },
  { platform: "yelp", label: "Yelp", sortOrder: 90 },
]);

const PLATFORM_KEYS = new Set(SOCIAL_PLATFORMS.map((entry) => entry.platform));

function normalizePlatform(platform) {
  const normalized = String(platform || "").trim().toLowerCase();
  if (normalized === "twitter") return "x";
  return normalized;
}

function getSocialPlatformConfig(platform) {
  const normalized = normalizePlatform(platform);
  return SOCIAL_PLATFORMS.find((entry) => entry.platform === normalized) || null;
}

function isSupportedSocialPlatform(platform) {
  return PLATFORM_KEYS.has(normalizePlatform(platform));
}

function hasProtocol(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function normalizeWebsiteUrl(input) {
  const value = String(input || "").trim();
  if (!value) return "";
  // Explicit schemes must be HTTP(S); never turn them into a hostname.
  if (/^[a-z][a-z\d+.-]*:/i.test(value) && !hasProtocol(value)) return "";
  try {
    const parsed = new URL(hasProtocol(value) ? value : `https://${value.replace(/^\/\//, "")}`);
    if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function parseUrl(value) {
  const normalized = normalizeWebsiteUrl(value);
  return normalized ? new URL(normalized) : null;
}

function isProfileUrl(value) {
  return hasProtocol(value) || /^(?:www\.)?(?:facebook\.com|instagram\.com|tiktok\.com|linkedin\.com|youtube\.com|youtu\.be|x\.com|twitter\.com|threads\.(?:net|com)|pinterest\.com|yelp\.com)(?:[/?#]|$)/i.test(value);
}

function normalizeSocialHandle(platform, input) {
  const normalizedPlatform = normalizePlatform(platform);
  let value = String(input || "").trim();
  if (!value) return "";

  const parsed = parseUrl(value);
  if (parsed && isProfileUrl(value)) {
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const lastPathPart = pathParts[pathParts.length - 1] || "";
    if (normalizedPlatform === "tiktok" || normalizedPlatform === "threads") {
      return lastPathPart ? `@${lastPathPart.replace(/^@+/, "")}` : "";
    }
    return lastPathPart ? lastPathPart.replace(/^@+/, "") : "";
  }

  value = value.replace(/^@+/, "").trim();
  if (!value) return "";

  if (normalizedPlatform === "tiktok" || normalizedPlatform === "threads") {
    return `@${value}`;
  }

  return value;
}

function buildSocialUrl(platform, input) {
  const normalizedPlatform = normalizePlatform(platform);
  const value = String(input || "").trim();
  if (!value || !isSupportedSocialPlatform(normalizedPlatform)) return "";

  if (isProfileUrl(value)) {
    const parsed = parseUrl(value);
    if (!parsed) return "";
    parsed.hash = "";
    return parsed.toString().replace(/\/$/, "");
  }
  if (/[:/\s?#]/.test(value)) return "";

  const handle = normalizeSocialHandle(normalizedPlatform, value).replace(/^@+/, "");
  if (!handle) return "";

  switch (normalizedPlatform) {
    case "facebook":
      return `https://facebook.com/${handle}`;
    case "instagram":
      return `https://instagram.com/${handle}`;
    case "tiktok":
      return `https://www.tiktok.com/@${handle}`;
    case "x":
      return `https://x.com/${handle}`;
    case "threads":
      return `https://www.threads.net/@${handle}`;
    case "pinterest":
      return `https://www.pinterest.com/${handle}`;
    case "yelp":
      return `https://www.yelp.com/biz/${handle}`;
    case "linkedin":
      return `https://linkedin.com/company/${handle}`;
    case "youtube":
      return `https://youtube.com/@${handle}`;
    default:
      return "";
  }
}

function validateSocialInput(platform, input) {
  const normalizedPlatform = normalizePlatform(platform);
  if (!isSupportedSocialPlatform(normalizedPlatform)) {
    return { valid: false, error: "Unsupported social platform." };
  }

  const value = String(input || "").trim();
  if (!value) return { valid: true, error: null };

  const url = buildSocialUrl(normalizedPlatform, value);
  if (!url) {
    if (normalizedPlatform === "linkedin" || normalizedPlatform === "youtube") {
      return { valid: false, error: "Enter a full profile URL for this platform." };
    }
    return { valid: false, error: "Enter a valid handle or profile URL." };
  }

  const parsed = parseUrl(url);
  if (!parsed) return { valid: false, error: "Enter a valid profile URL." };

  return { valid: true, error: null };
}

export {
  SOCIAL_PLATFORMS,
  normalizeWebsiteUrl,
  normalizePlatform,
  getSocialPlatformConfig,
  isSupportedSocialPlatform,
  normalizeSocialHandle,
  buildSocialUrl,
  validateSocialInput,
};
